import unionBy from 'lodash/unionBy';
import {
  ADD_FILES_SUCCESS,
  ADD_FILES_FAILURE,
  CLEAR_DEAL_STORE,
  DELETE_FILE_SUCCESS,
  FETCH_DEAL_ONLY_FILES_SUCCESS,
  FETCH_FILES_SUCCESS,
  FETCH_FILES_FAILURE,
  FETCH_UPLOADED_FILES_FAILURE,
  FETCH_VOUCHER_FILES_SUCCESS,
  IS_LOADING_DEAL_ONLY_FILES,
  IS_LOADING_FILES,
  IS_LOADING_VOUCHER_FILES,
  UPDATE_FILE_SUCCESS,
  UPDATE_FILE_FAILURE,
  ABSTRACTION_LEASE_FILE,
} from '../actions/action-types';

const initialFileSectionState = {
  count: 0,
  files: [],
  next: null,
  previous: null,
};

const initialState = {
  count: 0,
  error: undefined,
  files: [],
  voucherFiles: initialFileSectionState,
  dealOnlyFiles: initialFileSectionState,
  isLoadingFiles: false,
  isLoadingVoucherFiles: false,
  isLoadingDealOnlyFiles: false,
  next: null,
  previous: null,
  abstractionLeaseFile: {},
};

const getFileSection = sectionState => sectionState || initialFileSectionState;

const addFilesSuccess = (state, { payload: newFile }) => {
  const voucherFiles = getFileSection(state.voucherFiles);
  const dealOnlyFiles = getFileSection(state.dealOnlyFiles);
  return {
    ...state,
    files: [newFile, ...state.files],
    ...(newFile.is_lease_document === true && {
      voucherFiles: {
        ...voucherFiles,
        files: [newFile, ...voucherFiles.files],
      },
    }),
    ...(newFile.is_lease_document === false && {
      dealOnlyFiles: {
        ...dealOnlyFiles,
        files: [newFile, ...dealOnlyFiles.files],
      },
    }),
  };
};

const clearStore = () => ({
  ...initialState,
});

const deleteFileSuccess = (state, { payload: { id } }) => {
  const voucherFiles = getFileSection(state.voucherFiles);
  const dealOnlyFiles = getFileSection(state.dealOnlyFiles);
  return {
    ...state,
    files: state.files.filter(file => file.id !== id),
    voucherFiles: {
      ...voucherFiles,
      files: voucherFiles.files.filter(file => file.id !== id),
    },
    dealOnlyFiles: {
      ...dealOnlyFiles,
      files: dealOnlyFiles.files.filter(file => file.id !== id),
    },
  };
};

const filesFailure = (state, action) => ({
  ...state,
  error: action.payload.error,
});

const fetchFilesSuccess = (state, action) => {
  const {
    count,
    next,
    previous,
    results,
  } = action.payload;
  const files = previous === null
    ? results
    : unionBy(state.files, results, 'id');
  return ({
    ...state,
    count,
    files,
    next,
    previous,
  });
};

const fetchFileSectionSuccess = (sectionState, action) => {
  const currentSectionState = getFileSection(sectionState);
  const {
    count,
    next,
    previous,
    results,
  } = action.payload;
  const files = previous === null
    ? results
    : unionBy(currentSectionState.files, results, 'id');

  return {
    count,
    files,
    next,
    previous,
  };
};

const mergeFileSections = (voucherFiles, dealOnlyFiles, existingFiles) => (
  unionBy([
    ...getFileSection(voucherFiles).files,
    ...getFileSection(dealOnlyFiles).files,
    ...existingFiles,
  ], 'id')
);

const setSectionCount = (sectionState, count) => ({
  ...sectionState,
  count: Math.max(count, 0),
});

const updateVisibleSectionFile = (sectionState, file) => ({
  ...sectionState,
  files: sectionState.files.map(f => (f.id === file.id ? file : f)),
});

const removeVisibleSectionFile = (sectionState, fileId) => ({
  ...sectionState,
  files: sectionState.files.filter(f => f.id !== fileId),
});

const updateMovedFileSections = (voucherFiles, dealOnlyFiles, existingFile, file) => {
  const wasVoucherFile = existingFile?.is_lease_document === true
    || voucherFiles.files.some(f => f.id === file.id);
  const wasDealOnlyFile = existingFile?.is_lease_document === false
    || dealOnlyFiles.files.some(f => f.id === file.id);
  const isVoucherFile = file.is_lease_document === true;
  const isDealOnlyFile = file.is_lease_document === false;
  const visibleInVoucherFiles = voucherFiles.files.some(f => f.id === file.id);
  const visibleInDealOnlyFiles = dealOnlyFiles.files.some(f => f.id === file.id);
  let nextVoucherFiles = visibleInVoucherFiles
    ? updateVisibleSectionFile(voucherFiles, file)
    : voucherFiles;
  let nextDealOnlyFiles = visibleInDealOnlyFiles
    ? updateVisibleSectionFile(dealOnlyFiles, file)
    : dealOnlyFiles;

  if (wasVoucherFile && !isVoucherFile) {
    nextVoucherFiles = removeVisibleSectionFile(
      setSectionCount(nextVoucherFiles, nextVoucherFiles.count - 1),
      file.id,
    );
  } else if (!wasVoucherFile && isVoucherFile) {
    nextVoucherFiles = setSectionCount(nextVoucherFiles, nextVoucherFiles.count + 1);
  }

  if (wasDealOnlyFile && !isDealOnlyFile) {
    nextDealOnlyFiles = removeVisibleSectionFile(
      setSectionCount(nextDealOnlyFiles, nextDealOnlyFiles.count - 1),
      file.id,
    );
  } else if (!wasDealOnlyFile && isDealOnlyFile) {
    nextDealOnlyFiles = setSectionCount(nextDealOnlyFiles, nextDealOnlyFiles.count + 1);
  }

  return {
    voucherFiles: nextVoucherFiles,
    dealOnlyFiles: nextDealOnlyFiles,
  };
};

const fetchVoucherFilesSuccess = (state, action) => {
  const voucherFiles = fetchFileSectionSuccess(state.voucherFiles, action);
  return {
    ...state,
    voucherFiles,
    files: mergeFileSections(voucherFiles, state.dealOnlyFiles, state.files),
  };
};

const fetchDealOnlyFilesSuccess = (state, action) => {
  const dealOnlyFiles = fetchFileSectionSuccess(state.dealOnlyFiles, action);
  return {
    ...state,
    dealOnlyFiles,
    files: mergeFileSections(state.voucherFiles, dealOnlyFiles, state.files),
  };
};

const isLoadingFiles = (state, action) => ({
  ...state,
  isLoadingFiles: action.payload.isLoading,
});

const isLoadingVoucherFiles = (state, action) => ({
  ...state,
  isLoadingVoucherFiles: action.payload.isLoading,
});

const isLoadingDealOnlyFiles = (state, action) => ({
  ...state,
  isLoadingDealOnlyFiles: action.payload.isLoading,
});

const updateFileSuccess = (state, { payload: { file } }) => {
  const voucherFiles = getFileSection(state.voucherFiles);
  const dealOnlyFiles = getFileSection(state.dealOnlyFiles);
  const existingFile = state.files.find(f => f.id === file.id);
  const updatedSections = updateMovedFileSections(voucherFiles, dealOnlyFiles, existingFile, file);

  return {
    ...state,
    files: state.files.map(f => (f.id === file.id ? file : f)),
    ...updatedSections,
  };
};

const dealFiles = (state = initialState, action) => {
  switch (action.type) {
    case ADD_FILES_SUCCESS:
      return addFilesSuccess(state, action);
    case CLEAR_DEAL_STORE:
      return clearStore();
    case DELETE_FILE_SUCCESS:
      return deleteFileSuccess(state, action);
    case UPDATE_FILE_SUCCESS:
      return updateFileSuccess(state, action);
    case FETCH_FILES_SUCCESS:
      return fetchFilesSuccess(state, action);
    case FETCH_VOUCHER_FILES_SUCCESS:
      return fetchVoucherFilesSuccess(state, action);
    case FETCH_DEAL_ONLY_FILES_SUCCESS:
      return fetchDealOnlyFilesSuccess(state, action);
    case IS_LOADING_FILES:
      return isLoadingFiles(state, action);
    case IS_LOADING_VOUCHER_FILES:
      return isLoadingVoucherFiles(state, action);
    case IS_LOADING_DEAL_ONLY_FILES:
      return isLoadingDealOnlyFiles(state, action);
    case ADD_FILES_FAILURE:
    case FETCH_FILES_FAILURE:
    case FETCH_UPLOADED_FILES_FAILURE:
    case UPDATE_FILE_FAILURE:
      return filesFailure(state, action);
    case ABSTRACTION_LEASE_FILE:
      return { ...state, abstractionLeaseFile: action.payload.file };
    default:
      return state;
  }
};

export default dealFiles;

